/**
 * What is for sale and for how much: the one copy of the price list. The
 * seed creates it in Stripe (src/billing/stripe-seed.ts), the
 * pricing page and the dashboard's Settings print it, and checkout finds a
 * Price by its lookup key, so no `price_…` id is written down anywhere.
 *
 * Shared with the SPA, so nothing here may import server code.
 *
 * The numbers come from docs/research/plans-and-regional-pricing.md. A Stripe
 * Price cannot be edited: to change one, change it here, run the seed with a
 * new lookup key moved onto the new Price, and existing subscribers keep
 * what they pay.
 */
import type { PaidPlan } from './plan';
import { BRAND_NAME } from '../brand';

/**
 * The currencies with a hand-set price. Stripe Managed Payments converts the
 * default one (Adaptive Pricing) for a customer whose currency is not here,
 * at a rate that customer pays for.
 */
export const CURRENCIES = ['eur', 'usd', 'gbp', 'cad', 'aud'] as const;
export type Currency = (typeof CURRENCIES)[number];

/**
 * Every Price's own currency. The Stripe account is Bulgarian and settles in
 * euros, and Adaptive Pricing only converts from a settlement currency.
 */
export const DEFAULT_CURRENCY: Currency = 'eur';

/**
 * Whether the price a customer sees already holds the tax. Inclusive where
 * consumer prices are quoted that way (EU, UK, Australia); in the US and
 * Canada tax is added at checkout. Stripe sets this per currency, not per
 * country, and a converted currency takes the default's.
 */
export const TAX_INCLUSIVE: Record<Currency, boolean> = {
  eur: true,
  usd: false,
  gbp: true,
  cad: false,
  aud: true,
};

export const INTERVALS = ['month', 'year'] as const;
export type Interval = (typeof INTERVALS)[number];

/**
 * A first subscription's free trial, card required, while TRIAL_DAYS is
 * unset. Longer than 7 days on purpose: Stripe's merchant of record, Link,
 * emails the trial-ending reminder only for trials longer than that.
 */
export const DEFAULT_TRIAL_DAYS = 14;

/** The longest trial Stripe takes as `trial_period_days`. */
export const MAX_TRIAL_DAYS = 730;

/**
 * TRIAL_DAYS as configured: unset or blank is DEFAULT_TRIAL_DAYS, and
 * anything but a whole number of days Stripe takes is refused, since a
 * typo would otherwise change what checkout gives away. The server reads it
 * at run time (readTrialDays), the dashboard's build at build time
 * (web/vite.config.ts), both through here.
 */
export function parseTrialDays(raw: string | undefined): number {
  const value = raw?.trim();
  if (!value) return DEFAULT_TRIAL_DAYS;
  const days = Number(value);
  if (!/^\d+$/.test(value) || days < 1 || days > MAX_TRIAL_DAYS) {
    throw new Error(
      `TRIAL_DAYS must be a whole number of days from 1 to ${MAX_TRIAL_DAYS}; it is "${raw}".`,
    );
  }
  return days;
}

/**
 * The founding-member offer: a coupon, because the customer portal refuses
 * two Prices with the same Product and interval. It goes on annual plans
 * while STRIPE_FOUNDING_COUPON names it.
 */
export const FOUNDING = {
  coupon: 'FOUNDING25',
  percentOff: 25,
  maxRedemptions: 100,
} as const;

/**
 * "Website Information Services - Personal Use", one of the tax codes
 * eligible for Managed Payments. It only changes US sales tax.
 */
export const TAX_CODE = 'txcd_10701401';

/** The Stripe Product behind each paid plan; `metadata.plan` names the plan. */
export const PRODUCTS: Record<PaidPlan, { id: string; name: string }> = {
  ALERTS: { id: 'raise_sonar_alerts', name: `${BRAND_NAME} Alerts` },
  PRO: { id: 'raise_sonar_pro', name: `${BRAND_NAME} Pro` },
};

export interface CataloguePrice {
  plan: PaidPlan;
  interval: Interval;
  /** The Stripe Price's `lookup_key`. */
  lookupKey: string;
  /** In minor units (cents, pence), as Stripe takes them. */
  amounts: Record<Currency, number>;
}

export const CATALOGUE: CataloguePrice[] = [
  {
    plan: 'ALERTS',
    interval: 'month',
    lookupKey: 'alerts_monthly',
    amounts: { eur: 1500, usd: 1500, gbp: 1200, cad: 2000, aud: 2200 },
  },
  {
    plan: 'ALERTS',
    interval: 'year',
    lookupKey: 'alerts_annual',
    amounts: { eur: 12000, usd: 12000, gbp: 9900, cad: 15900, aud: 17900 },
  },
  {
    plan: 'PRO',
    interval: 'month',
    lookupKey: 'pro_monthly',
    amounts: { eur: 3500, usd: 3500, gbp: 2900, cad: 4900, aud: 5500 },
  },
  {
    plan: 'PRO',
    interval: 'year',
    lookupKey: 'pro_annual',
    amounts: { eur: 29000, usd: 29000, gbp: 23900, cad: 39900, aud: 44900 },
  },
];

/**
 * A sponsored slot in one Weekly Sonar issue (docs/features/sponsorship.md):
 * one-off, not a plan, and priced by hand in every currency the plans are:
 * €50, $50, £50, C$80, A$80. Built as a plan's Price is: the euro its
 * own currency, the others options, each with the plans' tax behaviour
 * (TAX_INCLUSIVE). Checkout charges a founder in their own currency where
 * it is one of these, and Adaptive Pricing converts the euro price for
 * anyone else. The booking page always shows the euro price.
 *
 * Not sold through Managed Payments, which refuses advertising: neither
 * Stripe tax code for it is eligible (tried in the sandbox on 23 Oct 2026).
 * Sponsor checkout is standard Checkout, where Raise Sonar is the merchant of
 * record and no tax is added, so the tax behaviour changes nothing charged
 * until Stripe Tax is turned on.
 */
export const SPONSOR_SLOT: {
  product: { id: string; name: string };
  lookupKey: string;
  taxCode: string;
  amounts: Record<Currency, number>;
} = {
  product: {
    id: 'raise_sonar_sponsor_slot',
    name: `${BRAND_NAME} sponsored slot`,
  },
  lookupKey: 'sponsor_slot',
  /** "Website Advertising": what is sold, for Stripe Tax should it be turned on. */
  taxCode: 'txcd_10701000',
  /** In minor units, as a plan's. */
  amounts: { eur: 5000, usd: 5000, gbp: 5000, cad: 8000, aud: 8000 },
};

export function priceOf(plan: PaidPlan, interval: Interval): CataloguePrice {
  const price = CATALOGUE.find(
    (p) => p.plan === plan && p.interval === interval,
  );
  // Every plan has both intervals; the table above is the proof.
  if (!price) throw new Error(`No price for ${plan} per ${interval}.`);
  return price;
}

/** The euro's members: Bulgaria joined on 1 January 2026. */
const EUROZONE = new Set([
  'AT',
  'BE',
  'BG',
  'CY',
  'DE',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'HR',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PT',
  'SI',
  'SK',
]);

const COUNTRY_CURRENCY: Record<string, Currency> = {
  US: 'usd',
  GB: 'gbp',
  CA: 'cad',
  AU: 'aud',
};

/**
 * The price list to show someone living in `country`: their own currency
 * where one is hand-set, else the euro's, which is what checkout converts.
 */
export function currencyFor(country: string | null | undefined): Currency {
  return (country && COUNTRY_CURRENCY[country]) || DEFAULT_CURRENCY;
}

/**
 * True when checkout charges `country` the listed amount in the listed
 * currency. Elsewhere Stripe converts the euro price, so the list is a guide.
 */
export function chargedAsListed(country: string | null | undefined): boolean {
  return !!country && (country in COUNTRY_CURRENCY || EUROZONE.has(country));
}

const SIGNS: Record<Currency, string> = {
  eur: '€',
  usd: '$',
  gbp: '£',
  cad: 'C$',
  aud: 'A$',
};

/** "€15", "£8.25", "C$159": cents only when the amount is not whole. */
export function formatAmount(minor: number, currency: Currency): string {
  const whole = minor % 100 === 0;
  const units = (minor / 100).toLocaleString('en-GB', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
  return `${SIGNS[currency]}${units}`;
}

/** What one month costs on this price: an annual price over twelve. */
export function perMonth(price: CataloguePrice, currency: Currency): number {
  const amount = price.amounts[currency];
  return price.interval === 'year' ? Math.round(amount / 12) : amount;
}

/** How much less a year costs paid annually than monthly, as a whole percent. */
export function annualSaving(plan: PaidPlan, currency: Currency): number {
  const monthly = priceOf(plan, 'month').amounts[currency] * 12;
  const annual = priceOf(plan, 'year').amounts[currency];
  return Math.round(((monthly - annual) / monthly) * 100);
}

/** A price after the founding coupon, in minor units. */
export function foundingAmount(minor: number): number {
  return Math.round((minor * (100 - FOUNDING.percentOff)) / 100);
}
